/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect } from 'react';

import { css as cssUnbounded, keyframes } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { AnimatedWrapper } from '@atlassian/ai-common/ui/gradient-wrapper';

import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { onAIAnswerLoadingDialogShown } from '../../../../ai-answer-dialog/on-ai-answer-loading-dialog-shown';
import type { commonAttributesType, SAINResearchStep } from '../../../../ai-answer-dialog/types';
import { useAnalytics } from '../../../../common/controller/analytics/use-analytics';

import { MorphingShapeLoader } from './morphing-shape-loader';
import { ResearchStepRow } from './research-step-row';
import { ShimmerText } from './shimmer-text';

/**
 * Pulls renderable research steps from the shared map, sorted oldest → newest.
 * Mirrors Chat: a step becomes visible as soon as its `RESEARCH_STEP_TITLE` is
 * received. The step's `completed` flag still controls visual styling (shimmer
 * while incomplete) but does not gate rendering, so users see step text
 * immediately rather than only briefly after `RESEARCH_STEP_COMPLETE`.
 */
const getRenderableSteps = (steps: { [stepId: string]: SAINResearchStep } | undefined) => {
	if (!steps) {
		return [];
	}
	return Object.values(steps)
		.filter((s) => !!s.title)
		.sort((a, b) => a.order - b.order);
};

const styles = cssMap({
	/**
	 * Outer container that wraps the loader row and the expanded research-step
	 * content. Given a fixed height so the loading card reserves consistent
	 * vertical space while research steps stream in. The sunken background +
	 * radius live here (not on the children) so the header row and research
	 * steps read as a single card, and `overflow: hidden` clips any steps that
	 * would otherwise extend past the fixed height.
	 */
	container: {
		display: 'flex',
		flexDirection: 'column',
		boxSizing: 'border-box',
		overflow: 'hidden',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
		// Fixed height, intentionally matched to the streaming state that replaces
		// this component in `smart-answer-card-consolidated.tsx` once the answer
		// starts streaming — i.e. `SmartAnswerCardBodyGenerating` (199px in the SAIN
		// UX uplift cohort, via the `sourcesSidePanelOverride` height) followed by
		// the 1px `AnimatedSingleLineShowMore` line. Keeping this equal to that body
		// height means the bottom edge lines up exactly with the show-more line, so
		// the loading → streaming transition shows no vertical shift at the bottom —
		// only the top/left/right borders of this card disappear. If the
		// body-generating height changes, update this value to match. Defined here
		// (not via the `style` prop) to satisfy the "no static values in the style
		// prop" lint rule.
		height: '199px',
	},
	shimmerWrapper: {
		flexGrow: 1,
		minWidth: '0px',
	},
	expandedContent: {
		// Grow to fill the remaining container height so the sunken background
		// always reaches the bottom edge, even before/without steps.
		flexGrow: 1,
		minHeight: '0px',
		paddingTop: token('space.0'),
		paddingBottom: token('space.150'),
		// Match the loader row's left padding so research steps left-align with
		// the MorphingShapeLoader above.
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		// Background + radius are provided by the parent `container` so the whole
		// card reads as one surface.
	},
	/**
	 * Skeleton placeholder row. Mirrors `collapsedRow`'s flex layout exactly — same
	 * columnGap, left/right padding, and a fixed 24px spacer column
	 * (`collapsedLoaderIconColumn`) standing in for the loader icon — so the
	 * skeleton lines in the second column left-align with the header's "Thinking
	 * deeper" text purely via structure. This avoids a `calc()` expression (which
	 * Compiled's `cssMap` types reject) while keeping the alignment token-driven.
	 */
	skeletonPlaceholder: {
		flexGrow: 1,
		minHeight: '0px',
		display: 'flex',
		flexDirection: 'row',
		columnGap: token('space.100'),
		paddingTop: token('space.0'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
	/** Second column of the skeleton row that holds the stacked skeleton lines. */
	skeletonLines: {
		flexGrow: 1,
		minWidth: '0px',
	},
	/**
	 * Loader row when expanded with steps — a flex row with stretched icon column
	 * so MorphingShapeLoader sits at top and the connector rule grows downward.
	 * Mirrors Chat's `substepRow`.
	 */
	/**
	 * Collapsed loader row — same flex structure and padding as expandedLoaderRow
	 * (including the 24px loaderIconColumn) so the MorphingShapeLoader sits at the
	 * exact same horizontal position in both collapsed and expanded states,
	 * preventing any layout shift when toggling.
	 */
	collapsedRow: {
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
		columnGap: token('space.100'),
		alignItems: 'center',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		boxSizing: 'border-box',
		height: '64px',
		flexShrink: 0,
		// Background + radius are provided by the parent `container`.
	},
	/**
	 * Loader icon column for the collapsed row — fixed 24px width to match the
	 * expanded loaderIconColumn (so no horizontal shift), but without
	 * alignSelf:stretch so the loader stays vertically centered with the text.
	 */
	collapsedLoaderIconColumn: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		flexShrink: 0,
	},
	expandedLoaderRow: {
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
		columnGap: token('space.100'),
		alignItems: 'stretch',
		// NOTE: paddingBottom is intentionally space.0 (not space.100 like
		// collapsedRow) so the connector line (`loaderRuleSlotWithLine`) reaches
		// the true bottom edge of the row and meets the first research step's top
		// connector with no gap. The text's vertical centering is instead
		// corrected on `loaderContent` (see its paddingBottom) so it doesn't shift.
		paddingTop: token('space.100'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		boxSizing: 'border-box',
		height: '64px',
		flexShrink: 0,
		// Background + radius are provided by the parent `container`.
	},
	/**
	 * Two-slot icon column: an empty top slot (flex-grow:1) above the loader
	 * and a connector slot below it. Centers the MorphingShapeLoader vertically
	 * in its row, exactly like `ResearchStepRow`'s icon column.
	 */
	loaderIconColumn: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		width: '24px',
		flexShrink: 0,
		alignSelf: 'stretch',
	},
	/**
	 * The top slot above the MorphingShapeLoader grows to vertically center the
	 * loader within its row — matching the centered text + button to its right.
	 */
	loaderRuleSlotTop: {
		flexGrow: 1,
		width: '0px',
	},
	loaderRuleSlotWithLine: {
		flexGrow: 1,
		width: '0px',
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		// The expanded loader row uses asymmetric vertical padding (paddingTop
		// space.100 / paddingBottom space.0) so the connector line reaches the
		// row's bottom edge. Without compensation, that 8px asymmetry centers the
		// MorphingShapeLoader ~4px lower than the collapsed state, misaligning it
		// with the "Thinking deeper" text. Giving this bottom slot an 8px flex
		// basis (while both slots still grow) makes it 8px taller than the top
		// slot, lifting the loader back up by ~4px — aligning it with the text —
		// while the line still grows all the way to the row's bottom edge (so the
		// connector to the first research step stays unbroken).
		flexBasis: token('space.100'),
	},
	/**
	 * Loader row main content (text + button) — uses flex-row with center
	 * alignment to vertically center everything in the row.
	 */
	loaderContent: {
		flexGrow: 1,
		minWidth: '0px',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		columnGap: token('space.100'),
		// The expanded loader row has no paddingBottom (so the connector line can
		// reach the row's bottom edge). That makes the row's centering box 8px
		// taller than collapsedRow's, which would push this text ~4px lower once
		// steps arrive. Add a matching bottom padding HERE (on the text content,
		// not the row) so the text centers over the same 48px box as the collapsed
		// state — keeping "Thinking deeper" at the exact same y-position — without
		// shortening the connector column beside it.
		paddingBottom: token('space.100'),
	},
});

const messages = defineMessages({
	thinking_deeper: {
		id: 'search-ai.smart-answer-card-minimized-loading.thinking-deeper',
		defaultMessage: 'Thinking deeper and analyzing your question...',
		description: 'Loading text shown while the AI is generating a deeper answer',
	},
});

type SmartAnswerCardMinimizedLoadingProps = {
	commonAttributes: commonAttributesType;
	testId?: string;
};

/** Max number of completed research steps to show beneath the loader in the expanded view. */
const MAX_EXPANDED_STEPS = 2;

const SKELETON_HEIGHT_LINE = '16px';
const SKELETON_BORDER_RADIUS = token('radius.medium');

/**
 * Placeholder skeleton lines shown beneath the header row while there are no
 * research steps yet, so the (fixed-height) loading card doesn't read as a large
 * empty box. Mirrors the borderless UX skeleton in `smart-answer-card-body-generating`
 * (two full-width lines + one 75% line).
 */
const skeletonLines: { width: string; height: string }[] = [
	{ width: '100%', height: SKELETON_HEIGHT_LINE },
	{ width: '100%', height: SKELETON_HEIGHT_LINE },
	{ width: '75%', height: SKELETON_HEIGHT_LINE },
];

/**
 * Entrance animation for a research step. Fades in with a small upward slide.
 * Because each step row is keyed by `stepId`, only a newly-mounted step runs
 * this mount animation — a surviving step that merely shifts position in the
 * rolling window keeps its DOM node and does NOT re-animate.
 */
const stepEnterKeyframes = keyframes({
	from: {
		opacity: 0,
		transform: 'translateY(8px)',
	},
	to: {
		opacity: 1,
		transform: 'translateY(0)',
	},
});

const stepEnterStyles = cssUnbounded({
	animationName: stepEnterKeyframes,
	animationDuration: '220ms',
	animationTimingFunction: 'ease-out',
	animationFillMode: 'both',
	// Respect users who prefer reduced motion: no slide/fade, appear instantly.
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});

export function SmartAnswerCardMinimizedLoadingV2({
	commonAttributes,
	testId,
}: SmartAnswerCardMinimizedLoadingProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [fireAnalyticsEvent] = useAnalytics();
	const { researchSteps } = useAIAnswerContext();

	useEffect(() => {
		fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const renderableSteps = getRenderableSteps(researchSteps);
	// Take the last MAX_EXPANDED_STEPS renderable steps in oldest-first order so
	// the most recent step appears at the bottom.
	const recentStepsForExpanded = renderableSteps.slice(-MAX_EXPANDED_STEPS);

	return (
		<AnimatedWrapper>
			<Box xcss={styles.container}>
				{/*
				 * Expanded loader row: uses a flex-column icon column so the
				 * MorphingShapeLoader sits at the top and the `loaderVerticalRule`
				 * grows to fill the space below — connecting tip-to-tip with the
				 * first research step's icon. Mirrors Chat's icon column pattern.
				 * Only renders the connector when there are steps to connect to.
				 */}
				{recentStepsForExpanded.length > 0 ? (
					<Box xcss={styles.expandedLoaderRow} testId={testId}>
						<Box xcss={styles.loaderIconColumn}>
							<Box xcss={styles.loaderRuleSlotTop} />
							<MorphingShapeLoader />
							<Box xcss={styles.loaderRuleSlotWithLine} />
						</Box>
						<Box xcss={styles.loaderContent}>
							<Box xcss={styles.shimmerWrapper}>
								<ShimmerText size="medium" truncate>
									{formatMessage(messages.thinking_deeper)}
								</ShimmerText>
							</Box>
						</Box>
					</Box>
				) : (
					<React.Fragment>
						<Box xcss={styles.collapsedRow} testId={testId}>
							<Box xcss={styles.collapsedLoaderIconColumn}>
								<MorphingShapeLoader />
							</Box>
							<Inline alignBlock="center" grow="fill" xcss={styles.shimmerWrapper}>
								<ShimmerText size="medium" truncate>
									{formatMessage(messages.thinking_deeper)}
								</ShimmerText>
							</Inline>
						</Box>
						{/* No research steps yet — show a skeleton placeholder so the
							fixed-height card doesn't read as a large empty box. The empty
							spacer column mirrors the loader icon column so the skeleton
							lines left-align with the header text (no calc needed). */}
						<Box
							xcss={styles.skeletonPlaceholder}
							testId={testId ? `${testId}-skeleton` : undefined}
						>
							<Box xcss={styles.collapsedLoaderIconColumn} />
							<Stack xcss={styles.skeletonLines} space="space.200">
								{skeletonLines.map((line, index) => (
									<Skeleton
										key={index}
										width={line.width}
										height={line.height}
										color={token('color.skeleton.subtle')}
										borderRadius={SKELETON_BORDER_RADIUS}
									/>
								))}
							</Stack>
						</Box>
					</React.Fragment>
				)}
				{recentStepsForExpanded.length > 0 && (
					<Box xcss={styles.expandedContent}>
						{recentStepsForExpanded.map((step, index) => (
							// Keyed by stepId so only newly-entering steps run the mount
							// (fade + slide) animation; surviving steps keep their DOM node.
							<div key={step.stepId} css={stepEnterStyles}>
								<ResearchStepRow
									step={step}
									// Top connector on every step (connects up to previous row /
									// loader); bottom connector on all but the last step.
									showTopConnector
									showBottomConnector={index < recentStepsForExpanded.length - 1}
									testId={testId ? `${testId}-step-${step.stepId}` : undefined}
								/>
							</div>
						))}
					</Box>
				)}
			</Box>
		</AnimatedWrapper>
	);
}
